import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuthenticatedUpload } from "@/hooks/useAuthenticatedUpload";
import { toast } from "sonner";
import { FileText, Image, Video, File, Upload, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ChallengeAttachment {
  id: string;
  name: string;
  type: "document" | "image" | "video" | "file";
  size: string;
  url: string;
}

interface ChallengeAttachmentUploadProps {
  userId: string;
  attachments: ChallengeAttachment[];
  onAttachmentsChange: (attachments: ChallengeAttachment[]) => void;
}

const getFileType = (file: File): ChallengeAttachment["type"] => {
  const mimeType = file.type;
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (
    mimeType.includes("pdf") ||
    mimeType.includes("document") ||
    mimeType.includes("word") ||
    mimeType.includes("excel") ||
    mimeType.includes("spreadsheet") ||
    mimeType.includes("presentation") ||
    mimeType.includes("powerpoint")
  ) {
    return "document";
  }
  return "file";
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const attachmentIcons = {
  document: FileText,
  image: Image,
  video: Video,
  file: File,
};

const attachmentColors = {
  document: "text-blue-500 bg-blue-500/10",
  image: "text-green-500 bg-green-500/10",
  video: "text-purple-500 bg-purple-500/10",
  file: "text-muted-foreground bg-secondary",
};

export const ChallengeAttachmentUpload = ({
  userId,
  attachments,
  onAttachmentsChange,
}: ChallengeAttachmentUploadProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  
  const { uploadFile, isUploading, handleError } = useAuthenticatedUpload({ 
    bucket: 'challenge-attachments' 
  });

  const processFiles = useCallback(async (files: FileList | File[]) => {
    if (!files || files.length === 0) return;

    const newAttachments: ChallengeAttachment[] = [];

    try {
      for (const file of Array.from(files)) {
        if (file.size > 50 * 1024 * 1024) {
          toast.error(`${file.name} is too large. Maximum size is 50MB.`);
          continue;
        }

        const fileExt = file.name.split(".").pop();
        const fileName = `${userId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        const result = await uploadFile(file, fileName);

        if (!result.success) {
          if (result.error === 'SESSION_EXPIRED') {
            // Session expiry is already handled by the hook
            return;
          }
          toast.error(`Failed to upload ${file.name}`);
          continue;
        }

        newAttachments.push({
          id: fileName,
          name: file.name,
          type: getFileType(file),
          size: formatFileSize(file.size),
          url: result.url!,
        });
      }

      if (newAttachments.length > 0) {
        onAttachmentsChange([...attachments, ...newAttachments]);
        toast.success(`${newAttachments.length} file(s) uploaded`);
      }
    } catch (error: any) {
      handleError(error, "Upload failed");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [userId, attachments, onAttachmentsChange, uploadFile, handleError]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      await processFiles(files);
    }
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set false if leaving the drop zone entirely
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processFiles(files);
    }
  }, [processFiles]);

  const handleRemoveAttachment = async (attachment: ChallengeAttachment) => {
    try {
      const { error } = await supabase.storage
        .from("challenge-attachments")
        .remove([attachment.id]);

      if (error) {
        console.error("Failed to delete file:", error);
      }

      onAttachmentsChange(attachments.filter((a) => a.id !== attachment.id));
      toast.success("Attachment removed");
    } catch (error) {
      console.error(error);
      onAttachmentsChange(attachments.filter((a) => a.id !== attachment.id));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">
          Attachments ({attachments.length})
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="gap-2"
        >
          {isUploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          {isUploading ? "Uploading..." : "Add Files"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.gif,.webp,.mp4,.mov,.zip"
        />
      </div>

      {/* Drop Zone */}
      <div
        ref={dropZoneRef}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !isUploading && fileInputRef.current?.click()}
        className={cn(
          "border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer",
          isDragOver
            ? "border-primary bg-primary/5 scale-[1.02]"
            : "border-border hover:border-primary/50 hover:bg-secondary/30",
          isUploading && "pointer-events-none opacity-60"
        )}
      >
        <div className={cn(
          "transition-transform",
          isDragOver && "scale-110"
        )}>
          {isUploading ? (
            <Loader2 className="w-10 h-10 text-primary mx-auto mb-3 animate-spin" />
          ) : (
            <Upload className={cn(
              "w-10 h-10 mx-auto mb-3 transition-colors",
              isDragOver ? "text-primary" : "text-muted-foreground"
            )} />
          )}
        </div>
        <p className={cn(
          "text-sm font-medium transition-colors",
          isDragOver ? "text-primary" : "text-foreground"
        )}>
          {isUploading 
            ? "Uploading files..." 
            : isDragOver 
              ? "Drop files here" 
              : "Drag & drop files here, or click to browse"}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          PDFs, images, documents, videos • Max 50MB per file
        </p>
      </div>

      {/* Attached Files */}
      {attachments.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-2">
          {attachments.map((attachment) => {
            const Icon = attachmentIcons[attachment.type];
            const colorClass = attachmentColors[attachment.type];
            return (
              <div
                key={attachment.id}
                className="flex items-center gap-3 px-3 py-2 bg-secondary/50 border border-border rounded-lg group"
              >
                <div className={cn("p-2 rounded-lg shrink-0", colorClass)}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {attachment.name}
                  </p>
                  <p className="text-xs text-muted-foreground">{attachment.size}</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveAttachment(attachment);
                  }}
                  className="p-1 rounded hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4 text-destructive" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
